import { Fragment, Suspense, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@fluxora/auth';
import { CrmDetailSkeleton } from './CrmSkeleton';

import { AccessDenied } from '../../pages/AccessDenied';
import { useLogout } from '../../lib/authStore';
import { filterNavItems, pathAllowed } from '../../lib/crmRouteAccess';
import { useCurrentOrg, type OrgBranding } from '../../lib/orgContext';
import { CrmAssistantDock, CrmAssistantLauncher, CrmAssistantProvider } from './CrmAssistantMount';
import { Ic } from './icons';
import { UserMenu } from './UserMenu';

interface RailItem {
  id: string;
  to: string;
  icon: JSX.Element;
  label: string;
  sub?: Array<{ id: string; label: string; to: string }>;
}

// ── Route + rail access ──────────────────────────────────────────────────────
// The route → claim matrix lives in `lib/crmRouteAccess.ts` (CLM-07b) and is the
// SINGLE source of truth for both the guard below and the rail: top-level items
// AND sub-items are filtered by it, so the rail never links to a page that would
// only render Access Denied. Legacy tokens (no `crm_perms_v`) keep the old
// per-prefix role lists, recorded beside each rule there.

interface RailGroup {
  section: string;
  items: RailItem[];
}

// Grouped nav (Workspace / Operations / System). Every original submenu is kept
// (Quotes, Billing, Pricing, Users). Only the dead placeholder utilities (Quick
// action, Calendar, Alerts, Settings — all `#`) were removed. Every `to`/`id`
// points at a real, seeded route: nav presentation only, no pages or seed data
// were touched.
/**
 * The rail, exported as data so `__tests__/CrmShellNav.test.tsx` can walk every
 * destination and assert `deriveActiveFromPath` maps it back to its own id.
 * Never restate this list in a test — a copy drifts and then proves nothing.
 */
export const NAV_GROUPS: RailGroup[] = [
  {
    section: 'Workspace',
    items: [
      { id: 'dashboard', to: '/dashboard', icon: <Ic.dashboard />, label: 'Dashboard' },
      // A leaf, not a group. Once Ending Soon went, the only child was
      // "All Accounts" pointing at /accounts — the same place the parent goes.
      // A disclosure arrow that expands to reveal one link to where you already
      // were is a click that buys nothing.
      { id: 'accounts', to: '/accounts', icon: <Ic.idCard />, label: 'Accounts' },
      /* LABELS ONLY.
       *
       * The section reads "Contracts and Signups" and its children follow,
       * because a quote in this product is the document a customer signs, not
       * a price enquiry. The `id`s and the `/quotes` routes are DELIBERATELY
       * unchanged: they are referenced by route definitions, deep links,
       * saved bookmarks, tests and the Quotation service's own paths, and
       * renaming them would be a behaviour change dressed as a copy change. */
      {
        id: 'quotes',
        to: '/quotes',
        icon: <Ic.doc />,
        label: 'Contracts and Signups',
        sub: [
          { id: 'view-quotes', label: 'View Contracts and Signups', to: '/quotes' },
          // Two generation paths, both in the rail. Quick Quote is the one-screen
          // path (auto-picks the cheapest priced plan, creates the quote
          // anonymously, hands off to the detail page). Quote Generation is the
          // marketplace: shop → compare → review, for when the plan needs
          // choosing rather than auto-selecting. The 4-step wizard stays routable
          // but has no rail entry — it is reached from Quick Quote.
          { id: 'quick-quote', label: 'Quick Signup', to: '/quotes/quick' },
          { id: 'marketplace', label: 'Signup Generation', to: '/quotes/marketplace' },
          { id: 'bulk-quote-jobs', label: 'Bulk Signup Jobs', to: '/quotes/bulk' },
        ],
      },
      // `briefcase`, not `share`. Three linked nodes is the glyph for "send this
      // somewhere", and `Ic.share` is this app's catch-all link mark in eight
      // other components — the rail was pointing at Brokers with a shape that
      // says nothing about them. A briefcase names the partner who sells on our
      // behalf, and it was already in the set, so the rail owns no new drawing
      // risk at the 16px it collapses to.
      { id: 'brokers', to: '/brokers', icon: <Ic.briefcase />, label: 'Brokers' },
      {
        id: 'billing',
        to: '/billing',
        icon: <Ic.receipt />,
        label: 'Billing',
        sub: [
          { id: 'billing-dash', label: 'Overview', to: '/billing' },
          { id: 'bills', label: 'Bills', to: '/billing/bills' },
          { id: 'billing-scheduler', label: 'Scheduler', to: '/billing/scheduler' },
          { id: 'billing-settings', label: 'Settings', to: '/billing/settings' },
        ],
      },
      // ADDED 2026-10-05, and it should have been here from the start: the
      // payments module had no rail entry at all, so /payment and its pages were
      // reachable only by typing the URL. The deferred-payment-plan and
      // average-billing screens had therefore never been found by anyone who did
      // not already know they existed.
      //
      // Worklist FIRST among the children. It is the only one that tells an
      // operator something they did not arrive looking for; the rest answer a
      // question about an account they already have in front of them.
      {
        id: 'payments',
        // The WORKLIST, not an overview. The overview page was removed on
        // 2026-10-07 — it listed the same destinations the rail already shows.
        to: '/payment/queue',
        icon: <Ic.coin />,
        label: 'Payments',
        // TWO entries, not nine. Collecting, refunding, fees, methods, AutoPay,
        // the payment plan and average billing were siblings here until
        // 2026-10-07; each one made the operator re-enter the reference they were
        // already working. They are tabs on the invoice now.
        sub: [
          { id: 'payments-list', label: 'Payments', to: '/payment' },
          { id: 'payments-queue', label: 'Worklist', to: '/payment/queue' },
        ],
      },
    ],
  },
  {
    section: 'Operations',
    items: [
      {
        id: 'pricing',
        to: '/pricing',
        icon: <Ic.tag />,
        label: 'Pricing',
        sub: [
          { id: 'rates-overview', label: 'Rates Overview', to: '/pricing/rates-overview' },
          { id: 'unit-rate', label: 'Unit Rate Catalogue', to: '/pricing' },
          // No "Tariffs" here any more. The catalogue is the first view of
          // the "Tariff management" entry below, beside the approvals queue
          // and the tariff managers it now belongs with; `/pricing/tariffs`
          // redirects there.
          { id: 'config', label: 'Pricing Config', to: '/pricing/config' },
        ],
      },
      // A LEAF, not a group: Tariffs, Approvals and Users are three views of
      // one page (`module/tariffs`, route tabs inside it), and a plan's life
      // — submitted, decided, published — is one story read from one place.
      // A submenu would offer three doors to one room, and the rail already
      // learned from Accounts what a disclosure arrow over one destination
      // buys. `check2` is a double tick: the section exists because a plan is
      // now signed off before it is sold.
      {
        id: 'tariffs',
        to: '/tariffs',
        icon: <Ic.check2 />,
        label: 'Tariff management',
      },
      // Hidden from the rail (2026-09-16, product request) — the routes still
      // exist and can be reached by URL; only the entries are commented out.
      // { id: 'metering', to: '/metering', icon: <Ic.bolt />, label: 'Metering' },
      // MS-7a. Hidden from everyone without `marketops.console.view` by the
      // `crmRouteAccess` rule — `pathAllowed` drives both the guard and this nav.
      // { id: 'market-ops', to: '/ops/market', icon: <Ic.pulse />, label: 'Market Ops' },
      // { id: 'journeys', to: '/journeys', icon: <Ic.share />, label: 'Journeys' },
      // Restored with the reports module (RPT-1..4). Gated by the `/reports`
      // rule in `crmRouteAccess` (`reports.all.view`).
      { id: 'reports', to: '/reports', icon: <Ic.reports />, label: 'Reports' },
    ],
  },
  {
    section: 'System',
    items: [
      {
        id: 'users',
        to: '/users',
        icon: <Ic.user />,
        label: 'Users',
        sub: [
          { id: 'users-list', label: 'Users', to: '/users' },
          { id: 'users-audit', label: 'Audit Log', to: '/users/audit' },
        ],
      },
      // NO SETTINGS ENTRY HERE, ON PURPOSE. Settings lives in the top-right
      // user menu (`UserMenu.tsx`), which has always been its door. E-DT04
      // briefly added a rail group too, and two front doors to one concept is
      // the actual problem — so the rail entry is the one that went.
      // Hidden from the rail (2026-09-16, product request); `/help` and `/docs`
      // still serve by URL.
      // { id: 'help', to: '/help', icon: <Ic.help />, label: 'User Manual' },
      // { id: 'docs', to: '/docs', icon: <Ic.docs />, label: 'Platform Docs' },
    ],
  },
];

/** Default brand block — shown when the tenant has no custom branding. */
function RailBrandDefault(): JSX.Element {
  return (
    <div className="crm-rail-brand">
      {/* The actual Rheora mark, not the generic stacked-layers glyph that
          stood in for it. It ships its own gradient, so it is an <img> rather
          than a `currentColor` inline SVG — recolouring it to the rail's
          foreground would flatten the brand to one tone. A tenant with its own
          branding still overrides this entirely (see RailBrandTenant). */}
      <span className="mark mark--logo">
        <img src="/brand/rheora-mark.svg" alt="" width={24} height={24} />
      </span>
      <span className="name">
        <span className="b1">Rheora</span>
        <span className="b2">Texas · ERCOT</span>
      </span>
    </div>
  );
}

/**
 * Tenant lettermark brand — driven by `country_us.tenant.branding` resolved
 * pre-login (see lib/orgContext). Renders the initials (e.g. RET) with the
 * accent letter tinted, the monogram tile, and the full name beneath — the
 * in-shell form of the tenant logo. Accent colour is dynamic tenant data, so it
 * is applied inline (not a design token).
 */
function RailBrandLettermark({ b }: { b: OrgBranding }): JSX.Element {
  const chars = [...(b.initials ?? '')];
  const accentIndex = typeof b.accentIndex === 'number' ? b.accentIndex : -1;
  const accent = typeof b.accentColor === 'string' ? b.accentColor : undefined;
  const monogram = chars[0] ?? '·';
  const nameLine = b.name ?? b.tagline ?? '';
  return (
    <div className="crm-rail-brand" title={b.name ?? undefined}>
      <span className="mark" aria-hidden="true">
        <span className="lm">{monogram}</span>
      </span>
      <span className="name">
        <span className="b1">
          {chars.map((c, i) => (
            <span
              key={`${c}-${i}`}
              // `is-accent` lets the console look (styles/crm.css) override
              // the inline colour on a brand-painted rail, where a tenant's
              // accent authored for a light rail can be unreadable.
              className={i === accentIndex ? 'is-accent' : undefined}
              {...(i === accentIndex && accent !== undefined ? { style: { color: accent } } : {})}
            >
              {c}
            </span>
          ))}
        </span>
        {nameLine !== '' ? <span className="b2">{nameLine}</span> : null}
      </span>
    </div>
  );
}

function CrmRail({
  active,
  activeSub,
}: {
  active?: string;
  activeSub?: string | null;
}): JSX.Element {
  // Driven by the SAME matrix as the route guard (`crmRouteAccess`), sub-items
  // included, so the rail never links to a page that would render Access Denied.
  const principal = useAuth().user;
  // Pre-login-resolved tenant branding (country_us.tenant.branding). When the
  // tenant ships a lettermark (e.g. RET), render it; otherwise fall back to the
  // generic default. Degrades safely on plain localhost / resolution failure.
  const branding = useCurrentOrg().currentOrg?.branding ?? null;
  // Which submenus are open. Default: the active item's submenu is open; the
  // caret button toggles any item, and an explicit entry here overrides the
  // active-by-default. (The collapsed rail hides submenus via CSS, so the caret
  // is only reachable once the rail expands on hover.)
  const [openOverrides, setOpenOverrides] = useState<Record<string, boolean>>({});
  const toggleSub = (id: string, activeDefault: boolean): void => {
    setOpenOverrides((prev) => ({ ...prev, [id]: !(prev[id] ?? activeDefault) }));
  };
  const logout = useLogout();
  const navigate = useNavigate();
  const handleLogout = (): void => {
    logout();
    navigate('/login', { replace: true });
  };
  return (
    <aside className="crm-rail">
      {branding !== null && branding.kind === 'lettermark' && (branding.initials ?? '') !== '' ? (
        <RailBrandLettermark b={branding} />
      ) : (
        <RailBrandDefault />
      )}

      <nav className="crm-rail-nav">
        {NAV_GROUPS.map((group) => {
          const items = filterNavItems(group.items, principal);
          if (items.length === 0) return null;
          return (
            <div className="crm-rail-group" key={group.section}>
              {items.map((it) => {
                const isActive = it.id === active;
                const isOpen = it.sub !== undefined && (openOverrides[it.id] ?? isActive);
                const inner = (
                  <>
                    <span className="icon-slot">{it.icon}</span>
                    <span className="lbl">{it.label}</span>
                    {it.sub && (
                      <span
                        className={`rail-sub-caret ${isOpen ? 'is-open' : ''}`}
                        aria-hidden="true"
                      >
                        <Ic.chev width={12} height={12} strokeWidth={2} />
                      </span>
                    )}
                  </>
                );
                return (
                  <Fragment key={it.id}>
                    {it.sub ? (
                      // Parent of a submenu: clicking ONLY toggles the submenu
                      // (no navigation). The real pages live in the sub-items.
                      <button
                        type="button"
                        className={`crm-rail-btn ${isActive ? 'is-active' : ''}`}
                        title={it.label}
                        aria-expanded={isOpen}
                        onClick={() => toggleSub(it.id, isActive)}
                      >
                        {inner}
                      </button>
                    ) : (
                      <Link
                        to={it.to}
                        className={`crm-rail-btn ${isActive ? 'is-active' : ''}`}
                        title={it.label}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        {inner}
                      </Link>
                    )}
                    {it.sub && isOpen && (
                      <div className="crm-rail-sub">
                        {it.sub.map((s) => (
                          <Link
                            key={s.id}
                            to={s.to}
                            className={`crm-rail-subitem ${s.id === activeSub ? 'is-active' : ''}`}
                          >
                            <span className="dot-mark" />
                            <span className="lbl">{s.label}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </Fragment>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="crm-rail-foot">
        <button
          type="button"
          className="crm-rail-btn crm-rail-logout"
          onClick={handleLogout}
          title="Log out"
          aria-label="Log out"
        >
          <span className="icon-slot">
            <Ic.logout />
          </span>
          <span className="lbl">Logout</span>
        </button>
      </div>
    </aside>
  );
}

/**
 * A breadcrumb is either a plain label — auto-linked when it names a known
 * top-level section (see `CRUMB_ROUTES`) — or an explicit `{ label, to }` for a
 * custom destination. The LAST crumb always renders as the current page and is
 * never a link.
 */
export type Crumb = string | { label: string; to?: string };

/**
 * Default routes for well-known section labels, so the existing string crumbs
 * (`['Home', 'Accounts', …]`) become navigable with no call-site changes.
 * Paths mirror the module route tables (see each module's `routes.tsx`).
 */
const CRUMB_ROUTES: Record<string, string> = {
  Home: '/dashboard',
  Dashboard: '/dashboard',
  Accounts: '/accounts',
  Quotes: '/quotes',
  Users: '/users',
  Pricing: '/pricing',
  Billing: '/billing',
};

/** Resolve a crumb's destination: explicit `to` wins, else the label map. */
function crumbTarget(c: Crumb): string | undefined {
  if (typeof c === 'string') return CRUMB_ROUTES[c];
  return c.to ?? CRUMB_ROUTES[c.label];
}

export interface CrmTopbarProps {
  crumbs?: Crumb[];
  title?: string;
  sub?: string;
  right?: ReactNode;
}

export function CrmTopbar({ crumbs, title, sub, right }: CrmTopbarProps): JSX.Element {
  return (
    <div className="crm-topbar">
      <div>
        {/* Breadcrumb removed from EVERY page (2026-08-17).
            It restated what the sidebar already highlights and what the page
            title already says — "Home › Quotes › Quick Quote" above a heading
            reading "Quick Quote" — and cost a row of vertical space on every
            screen for it.

            Removed here, in the single component that renders it, rather than at
            the 43 call sites that pass `crumbs`. The prop is still accepted and
            still typed, so no page needed touching and nothing 404s; it is
            simply not drawn. Uncommenting the block below restores it
            everywhere at once.

            `crumbTarget` and the `Link`/`Fragment` imports are retained for the
            same reason — they exist only for this block. */}
        {/*
        {crumbs && crumbs.length > 0 && (
          <div className="crm-breadcrumb">
            <svg
              className="home"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 11 12 3l9 8M5 10v10h14V10" />
            </svg>
            {crumbs.map((c, i) => {
              const label = typeof c === 'string' ? c : c.label;
              const isLast = i === crumbs.length - 1;
              const to = crumbTarget(c);
              return (
                <Fragment key={`${label}-${i}`}>
                  {i > 0 && (
                    <span className="sep">
                      <Ic.chevR width={10} height={10} strokeWidth={2} />
                    </span>
                  )}
                  {!isLast && to !== undefined ? (
                    <Link to={to} className="crumb crumb-link">
                      {label}
                    </Link>
                  ) : (
                    <span className={`crumb ${isLast ? 'is-cur' : ''}`}>{label}</span>
                  )}
                </Fragment>
              );
            })}
          </div>
        )}
        */}
        {title && <h1 className="crm-page-title">{title}</h1>}
        {sub && <div className="crm-page-sub">{sub}</div>}
      </div>
      <div className="crm-topbar-right">
        {/* Page-specific actions sit to the LEFT of the persistent account menu. */}
        {right}
        <UserMenu />
      </div>
    </div>
  );
}

export function deriveActiveFromPath(pathname: string): {
  active: string;
  activeSub: string | null;
} {
  if (pathname.startsWith('/billing/bills')) return { active: 'billing', activeSub: 'bills' };
  // `/billing/calendars` and `/billing/exceptions(/log)` are redirects to
  // Settings tabs since the Billing menu regroup; light Settings for them too,
  // so the one frame before the redirect lands does not flash Overview.
  if (pathname.startsWith('/billing/exceptions') || pathname.startsWith('/billing/calendars'))
    return { active: 'billing', activeSub: 'billing-settings' };
  if (pathname.startsWith('/billing/scheduler'))
    return { active: 'billing', activeSub: 'billing-scheduler' };
  if (pathname.startsWith('/billing/settings'))
    return { active: 'billing', activeSub: 'billing-settings' };
  if (pathname.startsWith('/billing')) return { active: 'billing', activeSub: 'billing-dash' };
  // ── Payments ───────────────────────────────────────────────────────────────
  // Longest prefix first, exactly as /billing above: every one of these also
  // starts with '/payment', so the catch-all has to come last or an operator on
  // Refunds would see Overview highlighted.
  //
  // `CrmShellNav.test.tsx` walks NAV_GROUPS and fails on any destination that
  // does not map back to its own id, which is how the missing /quotes/bulk and
  // /ops/market branches were each found. It caught this one the moment the rail
  // entry was added.
  if (pathname.startsWith('/payment/queue'))
    return { active: 'payments', activeSub: 'payments-queue' };
  // Everything else under /payment is the list or a detail page reached FROM it,
  // so both highlight the list — a detail page has no rail entry of its own, the
  // same arrangement Accounts uses for /accounts/:accountId.
  if (pathname.startsWith('/payment')) return { active: 'payments', activeSub: 'payments-list' };
  // Longest-prefix first: '/accounts/contracts-ending-soon' also starts with
  // '/accounts', so the generic branch below would swallow it.
  if (pathname.startsWith('/accounts/contracts-ending-soon'))
    return { active: 'accounts', activeSub: 'contracts-ending-soon' };
  if (pathname.startsWith('/accounts')) return { active: 'accounts', activeSub: 'all-accounts' };
  if (pathname.startsWith('/prospects/abandoned'))
    return { active: 'prospects', activeSub: 'prospects-abandoned' };
  if (pathname.startsWith('/prospects/deposit-pending'))
    return { active: 'prospects', activeSub: 'prospects-deposit' };
  if (pathname.startsWith('/prospects/switch-holds'))
    return { active: 'prospects', activeSub: 'prospects-switch' };
  if (pathname.startsWith('/prospects')) return { active: 'prospects', activeSub: 'prospects-all' };
  // The marketplace IS the rail's "Quote Generation" entry, and /quotes/shop is
  // the same surface under its newer path — both highlight it.
  if (pathname.startsWith('/quotes/marketplace') || pathname.startsWith('/quotes/shop'))
    return { active: 'quotes', activeSub: 'marketplace' };
  // Expiry & Renewal now lives in the profile menu's Settings popup, so the
  // rail has no entry for it. The route stays reachable (bookmarks, deep
  // links) and must still precede the catch-all /quotes branch — otherwise it
  // would false-highlight "All Quotes".
  if (pathname.startsWith('/quotes/settings')) return { active: 'quotes', activeSub: null };
  // Quick Quote is the rail's "Quote Generation" entry, so it highlights itself.
  if (pathname.startsWith('/quotes/quick')) return { active: 'quotes', activeSub: 'quick-quote' };
  // PRE-EXISTING GAP, found by CrmShellNav's every-destination test while adding
  // the /settings entries (E-DT04) — not introduced by that epic. The "Bulk
  // Quote Jobs" rail entry (`/quotes/bulk`) shipped in 80d9ea8 without a branch
  // here, so it fell through to the `/quotes` catch-all below and highlighted
  // "All Quotes" while the operator was on the bulk-job list. Same class of bug
  // as /ops/market and Brokers before it. `/quotes/bulk/:jobUId` is covered by
  // the same prefix on purpose: a job's detail page is still the bulk-jobs
  // destination.
  if (pathname.startsWith('/quotes/bulk'))
    return { active: 'quotes', activeSub: 'bulk-quote-jobs' };
  // The 4-step wizard stays routable as an in-page escape hatch (the "Full
  // wizard" button on Quick Quote) but has no rail entry — highlight the parent.
  if (pathname.startsWith('/quotes/new')) return { active: 'quotes', activeSub: null };
  if (pathname.startsWith('/quotes')) return { active: 'quotes', activeSub: 'view-quotes' };
  // Found by `CrmShellNav.test.tsx` on the day it was written — Brokers had
  // been highlighting Dashboard for exactly the same reason as /ops/market.
  if (pathname.startsWith('/brokers')) return { active: 'brokers', activeSub: null };
  if (pathname.startsWith('/journeys')) return { active: 'journeys', activeSub: null };
  if (pathname.startsWith('/pricing/rates-overview'))
    return { active: 'pricing', activeSub: 'rates-overview' };
  if (pathname.startsWith('/pricing/config')) return { active: 'pricing', activeSub: 'config' };
  // `/pricing/tariffs` is a redirect to `/tariffs` now (the catalogue moved
  // into Tariff management). It highlights its DESTINATION for the one render
  // before `<Navigate>` replaces it, so the rail does not flick through Unit
  // Rate Catalogue on the way — and it stays ahead of the bare `/pricing`
  // catch-all below, which would otherwise claim it.
  if (pathname.startsWith('/pricing/tariffs')) return { active: 'tariffs', activeSub: null };
  if (pathname.startsWith('/pricing')) return { active: 'pricing', activeSub: 'unit-rate' };
  // ── Tariff management ──────────────────────────────────────────────────────
  // One rail entry, three in-page route tabs (`/tariffs`, `/tariffs/approvals`,
  // `/tariffs/users`): every one of them is this leaf, so one prefix branch
  // covers them all and there is no sub to name.
  if (pathname.startsWith('/tariffs')) return { active: 'tariffs', activeSub: null };
  if (pathname.startsWith('/reports')) return { active: 'reports', activeSub: null };
  // ⚠️ EVERY rail destination NEEDS A CASE HERE. There is no automatic mapping
  // from `to` back to an id — a path with no branch falls through to the
  // `dashboard` default at the bottom, which lights up Dashboard while the user
  // is somewhere else entirely. `/ops/market` shipped without one and did
  // exactly that: the Market Ops page rendered correctly with Dashboard
  // highlighted beside it. Nothing failed loudly, because the fallback IS a
  // valid id. If you add a rail item, add a branch.
  //
  // `/ops/market/outbound` is covered by the same prefix on purpose: the
  // outbound delivery log is a VIEW of Market Ops reached from its tab strip,
  // not a rail destination of its own, so the rail must not change under it.
  if (pathname.startsWith('/ops/market')) return { active: 'market-ops', activeSub: null };
  // Same class of bug: the enrollment-issues console is the Quotation module's
  // ops surface and has no rail entry, so it highlighted Dashboard too. The
  // parent it belongs to is Quotes.
  if (pathname.startsWith('/ops/enrollment-issues')) return { active: 'quotes', activeSub: null };
  if (pathname.startsWith('/ops/metering')) return { active: 'metering', activeSub: null };
  if (pathname.startsWith('/metering')) return { active: 'metering', activeSub: null };
  if (pathname.startsWith('/users/audit')) return { active: 'users', activeSub: 'users-audit' };
  if (pathname.startsWith('/users')) return { active: 'users', activeSub: 'users-list' };
  // `/settings` has NO RAIL ENTRY, so nothing should highlight while the
  // operator is there — and `'none'` matches no item id, which is how that is
  // expressed.
  //
  // ⚠️ THE BRANCH IS STILL REQUIRED. Falling through to the ladder's
  // `dashboard` default would light up Dashboard while the user is in Settings —
  // the identical bug `/ops/market` and `/quotes/bulk` both shipped. And with
  // the nav entry gone, `CrmShellNav.test.tsx`'s walker no longer covers this
  // path, so it is pinned by a direct test instead.
  if (pathname.startsWith('/settings')) return { active: 'none', activeSub: null };
  if (pathname.startsWith('/help')) return { active: 'help', activeSub: null };
  if (pathname.startsWith('/docs')) return { active: 'docs', activeSub: null };
  return { active: 'dashboard', activeSub: null };
}

export function CrmShell(): JSX.Element {
  const location = useLocation();
  const { active, activeSub } = deriveActiveFromPath(location.pathname);
  const allowed = pathAllowed(location.pathname, useAuth().user);

  return (
    // The assistant provider wraps the shell so a conversation outlives route
    // changes — only <Outlet/> re-renders on navigation, so an operator can walk
    // off to verify an answer and come back to it. When the feature flag is off
    // this is a pass-through. See `CrmAssistantMount`.
    <CrmAssistantProvider>
      <div className="crm">
        <div className="crm-body">
          <CrmRail active={active} activeSub={activeSub} />
          <div className="crm-main">
            {/* THE shell fallback — every page inside the rail loads through here.
              It was a bare <Spinner> with no centring wrapper, so it rendered at
              the top-left of the content area, half under the header, looking
              like a stuck asset rather than a page arriving. The skeleton holds
              the shape the route is about to fill. */}
            <Suspense
              fallback={
                <div className="crm-page">
                  <CrmDetailSkeleton label="Loading page" />
                </div>
              }
            >
              {allowed ? <Outlet /> : <AccessDenied />}
            </Suspense>
          </div>
        </div>
        {/* Both the assistant card and its launcher live HERE, outside
            `.crm-body`, because both are `position: fixed` overlays.
            `.crm-body` is a two-column grid (rail | page) and nothing else
            belongs in it: the card floats above the page rather than taking a
            column, so the page does not reflow when it opens. An earlier
            revision docked the card as a third grid child — declaring a fixed
            overlay as a grid item is harmless by accident (a fixed element is
            not a grid item, so it claims no track) but it contradicts the
            two-track rule and reads as though the grid still has a third
            column. `.crm` sets no transform/filter, so `position: fixed`
            resolves against the viewport from this slot. */}
        <CrmAssistantDock />
        <CrmAssistantLauncher />
      </div>
    </CrmAssistantProvider>
  );
}
